import type { ComponentPropsWithoutRef } from 'react';
import type { DiscountBadgeAmountProps, DiscountBadgePercentProps, DiscountBadgeProps } from './const';
import { forwardRef } from 'react';
import { cn } from '../../utils';
import './index.less';

type SpanProps = Omit<ComponentPropsWithoutRef<'span'>, 'children'>;

function DiscountBoltIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="13" viewBox="0 0 11 13" fill="none" aria-hidden>
      <path d="M10.2825 8.34331L5.24291 12.0024L3.90192 10.4631L7.255 6.11072L10.2825 8.34331Z" fill="#FF6B27" />
      <path d="M1.63015 8.17766L0 7.35989L2.03769 0L7.33568 0.408883L1.63015 8.17766Z" fill="#FF6B27" />
    </svg>
  );
}

function shouldRenderDiscountBadge(props: DiscountBadgeProps) {
  if (props.variant === 'amount') {
    return props.amount > 0;
  }

  return props.discount > 0;
}

function formatDiscountBadgeText(props: DiscountBadgeProps) {
  if (props.label) {
    return props.label;
  }

  if (props.variant === 'amount') {
    return `-${props.amount}`;
  }

  return `${props.discount}%OFF`;
}

function getSpanProps(props: DiscountBadgeProps): SpanProps {
  if (props.variant === 'amount') {
    const { variant, amount, label, ...spanProps } = props;
    return spanProps;
  }

  const { variant, discount, label, ...spanProps } = props;
  return spanProps;
}

function DiscountBadge(props: DiscountBadgeProps, ref: React.Ref<HTMLSpanElement>) {
  if (!shouldRenderDiscountBadge(props)) {
    return null;
  }

  const { className } = props;
  const spanProps = getSpanProps(props);

  return (
    <span
      {...spanProps}
      ref={ref}
      className={cn('wg-discount-badge', className)}
    >
      <span className="wg-discount-badge__body">
        <span className="wg-discount-badge__shine" aria-hidden>
          <span className="wg-discount-badge__shine-line wg-discount-badge__shine-line--left" />
          <span className="wg-discount-badge__shine-line wg-discount-badge__shine-line--right" />
        </span>
        <span className="wg-discount-badge__text">
          {formatDiscountBadgeText(props)}
        </span>
      </span>
      <span className="wg-discount-badge__bolt" aria-hidden>
        <DiscountBoltIcon />
      </span>
    </span>
  );
}

const DiscountBadgeComponent = forwardRef<HTMLSpanElement, DiscountBadgeProps>(DiscountBadge);

DiscountBadgeComponent.displayName = 'DiscountBadge';

export default DiscountBadgeComponent;
export type { DiscountBadgeAmountProps, DiscountBadgePercentProps, DiscountBadgeProps };
